import { FlowAction, OnlineReservationInListDTO, OnSelectLocationAction, TicketLocationDTO, TicketSummaryDTO } from 'core/api';
import { Result, SessionProvider } from 'core/infrastructure';
import { Command } from 'pos/common';
import { CustomerDisplay, UserActivityMonitor } from 'pos/services';
import * as SalesStore from '../SalesStore';
import autoPrepareIfNeeded from '../utils/autoPrepareIfNeeded';
import { userCanClaimTicket } from 'pos/modules/helpers/ticket-selection';
import { OrderingFacade } from 'pos/facades';
import makeTicketReadyToSale from '../ticket/makeTicketReadyToSale';
import startTakeOutOrderIfNeeded from '../utils/startTakeOutOrderIfNeeded';
import { selectOpenTicket } from './selectOpenTicket';

function isValidTicket(ticket: TicketSummaryDTO, locationAction: OnSelectLocationAction): boolean {
	switch (locationAction) {
		case OnSelectLocationAction.AlwaysCreateNewTicket:
			return false;
		case OnSelectLocationAction.LoadByUserOrCreateNewTicket:
			return ticket.OpenerUser === SessionProvider.getCurrent().UserName;
		case OnSelectLocationAction.LoadOpenedOrCreateNewTicket:
			return true;
		default: throw new Error(`OnSelectSaleLocation desconocido ${locationAction}`);
	}
}

async function loadTicketIn(location: TicketLocationDTO, isOnlineReservationSelected: boolean, locationAction: OnSelectLocationAction): Promise<'created-new' | 'loaded-existing' | 'cancel'> {

	// Siempre que hay una reserva seleccionada, creamos un nuevo ticket para asociar a esa reserva
	if (isOnlineReservationSelected) {
		await SalesStore.createNewTicket(location);
		return 'created-new';
	}

	let tickets = await OrderingFacade.getExistingTicketsIn(location.SaleCenterId, location.SaleLocationName);
	tickets = tickets.filter(t => isValidTicket(t, locationAction));

	const result = await selectOpenTicket(tickets);
	if (!result.ok)
		return 'cancel';

	if (result.value.select === 'new') {
		await SalesStore.createNewTicket(location);
		return 'created-new';
	}

	const ticket = result.value.ticket!;

	const canClaim = await userCanClaimTicket(ticket);
	if (!canClaim)
		return 'cancel';

	await SalesStore.loadTicketById(ticket.Id);

	return 'loaded-existing';
}

export async function initSale() {

	await CustomerDisplay.displayTicketTotal(SalesStore.getTicket());

	const result = await Command.execute('initSale');
	if (!result.ok)
		return Result.error();

	await autoPrepareIfNeeded({ ticket: SalesStore.getTicket(), onTicketPrepared: ticket => SalesStore.loadTicketById(ticket.Id) });
	await SalesStore.release();
	SalesStore.applyDefaultSeatNumber();

	return Result.ok(result.value);
}

export async function initSaleOnLocation(location: TicketLocationDTO, reservation: Nullable<OnlineReservationInListDTO>, locationAction: OnSelectLocationAction): Promise<Result<FlowAction>> {

	// Registro que hay activad de usario mientras lanzo estas operacions, por ejemplo si hay que establecer comensales o friendlyName y se queda mucho rato ahí,
	// puede darle tiempo a cerrar esta pantalla o no en un session:timeout y queda raro, por el contario si no le da tiempo probablemente nada más entrar en la de venta se va a cerrar
	// porque lleva mucho tiempo inactivo.
	const result = await UserActivityMonitor.registerWhile(async () => {

		const loaded = await loadTicketIn(location, reservation !== null, locationAction);
		if (loaded === 'cancel')
			return Result.error();

		if (loaded === 'created-new') {

			const assignResult = await Command.execute('assignReservation', reservation);
			if (!assignResult.ok) {
				await SalesStore.release();
				return Result.error();
			}

			const makeReadyResult = await makeTicketReadyToSale({
				getTicket: SalesStore.getTicket,
				setFriendlyName: SalesStore.setFriendlyName,
				setTableCompanions: SalesStore.setTableCompanions
			});
			if (!makeReadyResult.ok) {
				await SalesStore.release();
				return Result.error();
			}
		}

		const startTakeOutOrderIfNeededResult = await startTakeOutOrderIfNeeded(SalesStore.getTicket());
		if (!startTakeOutOrderIfNeededResult.ok) {
			await SalesStore.release();
			return Result.error();
		}

		return Result.ok();

	});

	if (!result.ok)
		return Result.error();

	return initSale();
}